import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import { emailAPI } from '../utils/api';
import { RefreshCw, Mail, CheckCircle, XCircle, Clock, AlertTriangle, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';

const EMAIL_STATUS_CONFIG = {
  sent: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
  queued: { color: 'bg-blue-100 text-blue-800', icon: Clock },
  failed: { color: 'bg-red-100 text-red-800', icon: XCircle },
  retrying: { color: 'bg-yellow-100 text-yellow-800', icon: RefreshCw },
  logged_only: { color: 'bg-gray-100 text-gray-800', icon: AlertTriangle },
};

const EMAIL_TYPE_LABELS = {
  client_welcome: 'Welcome Email',
  agreement_sent: 'Agreement Sent',
  agreement_signed: 'Agreement Signed',
  document_checklist_unlocked: 'Checklist Unlocked',
  document_rejected: 'Document Rejected',
  document_approved: 'Document Approved',
  pending_document_reminder: 'Document Reminder',
  final_application_ready: 'Application Ready',
  visa_fee_due: 'Visa Fee Due',
  visa_application_submitted: 'Application Submitted',
  visa_decision_received: 'Visa Decision',
  officer_new_case_assigned: 'Case Assigned',
  officer_document_uploaded: 'Document Uploaded',
  officer_change_request: 'Change Request',
  officer_application_approved: 'Application Approved',
  officer_documents_completed: 'Documents Complete',
};

export default function EmailLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ skip: 0, limit: 20, total: 0 });
  const [filters, setFilters] = useState({
    status: '',
    email_type: '',
    search: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [retrying, setRetrying] = useState(null);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        skip: pagination.skip,
        limit: pagination.limit,
      };
      if (filters.status) params.status = filters.status;
      if (filters.email_type) params.email_type = filters.email_type;

      const [logsRes, statsRes] = await Promise.all([
        emailAPI.getLogs(params),
        emailAPI.getStats(),
      ]);

      setLogs(logsRes.data.logs || []);
      setPagination(prev => ({ ...prev, total: logsRes.data.total || 0 }));
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching email logs:', error);
      toast.error('Failed to load email logs');
    } finally {
      setLoading(false);
    }
  }, [pagination.skip, pagination.limit, filters.status, filters.email_type]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleRetry = async (emailId) => {
    try {
      setRetrying(emailId);
      await emailAPI.retryEmail(emailId);
      toast.success('Email queued for retry');
      fetchLogs();
    } catch (error) {
      toast.error('Failed to retry email');
    } finally {
      setRetrying(null);
    }
  };

  const handleProcessQueue = async () => {
    try {
      await emailAPI.processQueue();
      toast.success('Processing email queue...');
      fetchLogs();
    } catch (error) {
      toast.error('Failed to process queue');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const StatusBadge = ({ status }) => {
    const config = EMAIL_STATUS_CONFIG[status] || EMAIL_STATUS_CONFIG.queued;
    const Icon = config.icon;
    return (
      <span className={`${config.color} px-2.5 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status}
      </span>
    );
  };

  const filteredLogs = logs.filter(log => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        log.to?.toLowerCase().includes(search) ||
        log.subject?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content" data-testid="email-logs-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Email Logs</h1>
            <p className="text-slate-500 text-sm">Monitor all email communications</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-all flex items-center gap-2"
            >
              <Filter size={18} />
              Filters
            </button>
            <button
              onClick={fetchLogs}
              className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-all flex items-center gap-2"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              onClick={handleProcessQueue}
              className="px-4 py-2 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-all flex items-center gap-2"
            >
              <Mail size={18} />
              Process Queue
            </button>
            <div className="w-px h-8 bg-slate-200"></div>

          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
              <div className="text-sm text-slate-500">Total Emails</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-2xl font-bold text-green-600">{stats.sent}</div>
              <div className="text-sm text-slate-500">Sent</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-2xl font-bold text-blue-600">{stats.queued}</div>
              <div className="text-sm text-slate-500">Queued</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-2xl font-bold text-yellow-600">{stats.retrying}</div>
              <div className="text-sm text-slate-500">Retrying</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-2xl font-bold text-red-600">{stats.failed}</div>
              <div className="text-sm text-slate-500">Failed</div>
            </div>
          </div>
        )}

        {/* Filters */}
        {showFilters && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search by email or subject..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900 outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                <select
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900 outline-none"
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="">All Statuses</option>
                  <option value="sent">Sent</option>
                  <option value="queued">Queued</option>
                  <option value="failed">Failed</option>
                  <option value="retrying">Retrying</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email Type</label>
                <select
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900 outline-none"
                  value={filters.email_type}
                  onChange={(e) => setFilters(prev => ({ ...prev, email_type: e.target.value }))}
                >
                  <option value="">All Types</option>
                  {Object.entries(EMAIL_TYPE_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => setFilters({ status: '', email_type: '', search: '' })}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-all"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Email Logs Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">Email History</h2>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Recipient</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Subject</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Type</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Status</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Sent At</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div className="text-sm font-medium text-slate-900">{log.to}</div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm text-slate-600 max-w-xs truncate" title={log.subject}>
                          {log.subject}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="px-2.5 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">
                          {EMAIL_TYPE_LABELS[log.email_type] || log.email_type}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <StatusBadge status={log.status} />
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-500">
                        {formatDate(log.sent_at || log.created_at)}
                      </td>
                      <td className="py-3 px-4">
                        {(log.status === 'failed' || log.status === 'logged_only') && (
                          <button
                            onClick={() => handleRetry(log.id)}
                            disabled={retrying === log.id}
                            className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition-all disabled:opacity-50 flex items-center gap-1"
                          >
                            {retrying === log.id ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <RefreshCw className="h-4 w-4" />
                                Retry
                              </>
                            )}
                          </button>
                        )}
                        {log.error_message && (
                          <span className="text-xs text-red-600 ml-2" title={log.error_message}>
                            Error
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredLogs.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <Mail size={48} className="mx-auto mb-4 text-slate-300" />
                  <p>No email logs found</p>
                </div>
              )}

              {/* Pagination */}
              {pagination.total > pagination.limit && (
                <div className="flex justify-between items-center p-4 border-t border-slate-200">
                  <div className="text-sm text-slate-500">
                    Showing {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
                  </div>
                  <div className="flex gap-2">
                    <button
                      disabled={pagination.skip === 0}
                      onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip - prev.limit }))}
                      className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      disabled={pagination.skip + pagination.limit >= pagination.total}
                      onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
                      className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
